/**
 * Godot's tonemap pass as one effect: the tone curve, plus the glow gather and blend under
 * `FLAG_USE_GLOW` and the colour adjustments under `FLAG_USE_BCS`, as in `tonemap.glsl`. One shader
 * lets SOFTLIGHT composite after the curve, with the glow buffer tonemapped, and every other mode
 * before it, which a fixed pass order cannot express.
 */
import { BlendFunction, Effect } from 'postprocessing';
import * as THREE from 'three';
import { glowLevelCap, glowLevelSize } from '../../resources/environment/godotGlow';
import { autoExposedFirstLevelBlurGlsl, BLUR_BLURRED_UNIFORM, BLUR_SOURCE_UNIFORM, firstLevelBlurGlsl, GLOW_TEXTURE_SIZE_UNIFORM, glowLevelUniform, laterLevelBlurGlsl, } from '../../resources/environment/godotGlowPyramid';
import { compositeProgram, GLOW_MAP_UNIFORM, } from '../../resources/environment/godotCompositor';
import { LUMINANCE_UNIFORM } from '../../resources/environment/godotLuminance';
import { defaultWhiteTexture } from '../materials/defaultWhiteTexture';
import { drawFullScreen, fullScreenPass } from '../fullScreenPass';
import { drawOffscreen } from '../offscreen';
import { createPassTarget } from './passTarget';
import { LuminanceReduction } from './LuminanceReduction';
export class GodotToneMapEffect extends Effect {
    /** Null when the environment has no glow: nothing to gather, nothing to own. */
    pyramid;
    /** Null when the camera attributes hold no auto exposure. */
    luminance;
    constructor(options) {
        const { fragmentShader, uniforms } = compositeProgram(options);
        super('GodotToneMapEffect', fragmentShader, {
            // The effect writes the finished frame, glow and curve applied, so the effect pass must not
            // blend it into the scene again.
            blendFunction: BlendFunction.SRC,
            uniforms: new Map(Object.entries(uniforms).map(([name, value]) => [name, new THREE.Uniform(value)])),
        });
        this.clearGlowMap();
        this.luminance = options.autoExposure && new LuminanceReduction(options.autoExposure);
        if (this.luminance)
            this.uniforms.get(LUMINANCE_UNIFORM).value = this.luminance.texture;
        this.pyramid = options.glow
            ? new GlowPyramid(options.glow, this.uniforms, this.luminance?.gainUniforms ?? null)
            : null;
    }
    /** Samples `map` as the glow map. The caller owns `map`. A no-op when the composite samples no map. */
    setGlowMap(map) {
        const uniform = this.uniforms.get(GLOW_MAP_UNIFORM);
        if (uniform)
            uniform.value = map;
    }
    /** Samples white, which leaves the glow unscaled: the state while the map loads. */
    clearGlowMap() {
        this.setGlowMap(defaultWhiteTexture());
    }
    setSize(width, height) {
        this.pyramid?.setSize(width, height);
    }
    /**
     * Reduces the frame to its luminance, then builds the pyramid the composite shader gathers from,
     * since the bright pass reads the luminance (`renderer_scene_render_rd.cpp:551-607`).
     */
    update(renderer, inputBuffer) {
        this.luminance?.reduce(renderer, inputBuffer);
        this.pyramid?.build(renderer, inputBuffer);
    }
    dispose() {
        this.luminance?.dispose();
        this.pyramid?.dispose();
        super.dispose();
    }
}
/**
 * What Godot samples of the glow map: the raw texels, through the texture's non-sRGB view
 * (`renderer_scene_render_rd.cpp:707`), clamped (`tone_mapper.cpp:177`).
 */
export const GLOW_MAP_SAMPLER = {
    colorSpace: THREE.NoColorSpace,
    wrap: THREE.ClampToEdgeWrapping,
};
/**
 * The glow levels and the passes that build them, owned as `BloomEffect` owns its own. Not a
 * `BloomEffect`: Godot weights its seven levels apart (`[0, 0.8, 0.4, 0.1, 0, 0, 0]` by default),
 * while `MipmapBlurPass` has one radius for every level, which gives a haze instead of a halo.
 */
class GlowPyramid {
    compositeUniforms;
    levelTargets = [];
    /** Each level's horizontal blur, which its vertical pass reads. */
    horizontalTargets = [];
    firstBlur;
    laterBlur;
    maxLevel;
    gatheredLevels;
    /** The highest level built and read for the frame size, which `setSize` lowers for a small frame. */
    topLevel;
    /** The composite's size uniform, which `setSize` writes. Null under the Linear upscale mode. */
    textureSize;
    /** Binds each gathered level's sampler in `compositeUniforms`, and rebinds it on every resize. */
    constructor(glow, compositeUniforms, autoExposureUniforms) {
        this.compositeUniforms = compositeUniforms;
        // `glowParamsFor` returns null rather than an empty pyramid, so a `GlowParams`
        // that exists always has at least one weighted level.
        this.maxLevel = glow.maxLevel;
        this.topLevel = glow.maxLevel;
        this.gatheredLevels = glow.gatheredLevels;
        this.firstBlur = autoExposureUniforms
            ? new LevelBlur('First', autoExposedFirstLevelBlurGlsl(glow), autoExposureUniforms)
            : new LevelBlur('First', firstLevelBlurGlsl(glow));
        this.laterBlur = new LevelBlur('Later', laterLevelBlurGlsl(glow));
        for (let level = 0; level <= this.maxLevel; level++) {
            this.levelTargets.push(createPassTarget(`GodotGlow.Level${level}`));
            this.horizontalTargets.push(createPassTarget(`GodotGlow.Horizontal${level}`));
        }
        const sizeUniform = compositeUniforms.get(GLOW_TEXTURE_SIZE_UNIFORM);
        this.textureSize = sizeUniform ? (sizeUniform.value = new THREE.Vector2()) : null;
        this.rebindLevels();
    }
    /**
     * A level past the cap reads the last level there is. Godot's `textureLod` clamps a lod to the
     * last mip of the glow texture the same way (`tonemap.glsl:384-406`).
     */
    rebindLevels() {
        for (const level of this.gatheredLevels) {
            this.compositeUniforms.get(glowLevelUniform(level)).value =
                this.levelTargets[Math.min(level, this.topLevel)].texture;
        }
    }
    setSize(width, height) {
        this.topLevel = Math.min(this.maxLevel, glowLevelCap(width, height));
        const glowTexture = glowLevelSize(width, height, 0);
        this.textureSize?.set(glowTexture.width, glowTexture.height);
        for (let level = 0; level < this.levelTargets.length; level++) {
            const size = glowLevelSize(width, height, level);
            this.levelTargets[level].setSize(size.width, size.height);
            this.horizontalTargets[level].setSize(size.width, size.height);
        }
        this.rebindLevels();
    }
    /** Blurs the frame into level 0 and each level into the next (`renderer_scene_render_rd.cpp:603-613`). */
    build(renderer, inputBuffer) {
        drawOffscreen(renderer, () => {
            for (let level = 0; level <= this.topLevel; level++) {
                const source = level === 0 ? inputBuffer : this.levelTargets[level - 1];
                const blur = level === 0 ? this.firstBlur : this.laterBlur;
                blur.draw(renderer, source, this.horizontalTargets[level], this.levelTargets[level]);
            }
        });
    }
    dispose() {
        for (const target of [...this.levelTargets, ...this.horizontalTargets]) {
            target.dispose();
        }
        this.firstBlur.dispose();
        this.laterBlur.dispose();
    }
}
/** One level's two passes: each pass reads the texture the one before wrote. */
class LevelBlur {
    horizontal;
    vertical;
    /** `verticalUniforms` are the ones the vertical pass reads besides the horizontal result. */
    constructor(name, glsl, verticalUniforms = {}) {
        this.horizontal = fullScreenPass(`GodotGlow.${name}Horizontal`, glsl.horizontal, {
            [BLUR_SOURCE_UNIFORM]: { value: null },
        });
        this.vertical = fullScreenPass(`GodotGlow.${name}Vertical`, glsl.vertical, {
            [BLUR_BLURRED_UNIFORM]: { value: null },
            ...verticalUniforms,
        });
    }
    draw(renderer, source, horizontalTarget, destination) {
        this.horizontal.uniforms[BLUR_SOURCE_UNIFORM].value = source.texture;
        drawFullScreen(renderer, this.horizontal, horizontalTarget);
        this.vertical.uniforms[BLUR_BLURRED_UNIFORM].value = horizontalTarget.texture;
        drawFullScreen(renderer, this.vertical, destination);
    }
    dispose() {
        this.horizontal.dispose();
        this.vertical.dispose();
    }
}
//# sourceMappingURL=GodotToneMapEffect.js.map